import { useState } from 'react'
import { FiLock, FiLoader, FiAlertCircle, FiCheck, FiLogOut } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

// Troca de senha obrigatória.
//
// Ela substitui o app inteiro em vez de ser um modal por cima: com a troca
// pendente o servidor responde 423 em tudo, então mostrar o sistema atrás
// seria oferecer uma tela que não funciona. Aqui não há "depois" nem X de
// fechar — a única saída além de trocar é sair.
//
// A régua vem do servidor (lib/senha.cjs) e é repetida aqui só como AJUDA
// enquanto se digita. Quem valida é lá: dica de tela não é regra.

const MIN = 10

export function TrocaSenhaObrigatoria() {
  const { authenticatedFetch, logout, user, marcaSenhaTrocada } = useAuth()
  const [atual, setAtual] = useState('')
  const [nova, setNova] = useState('')
  const [confirma, setConfirma] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const curta = nova.length > 0 && nova.length < MIN
  const diferente = confirma.length > 0 && nova !== confirma
  const podeSalvar = atual.length > 0 && nova.length >= MIN && nova === confirma && !salvando

  const envia = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!podeSalvar) return
    setErro(null); setSalvando(true)
    try {
      const r = await authenticatedFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ senhaAtual: atual, novaSenha: nova }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.success) throw new Error(d.error || 'não consegui trocar a senha')
      marcaSenhaTrocada()
    } catch (e) {
      setErro((e as Error).message)
      setSalvando(false)
    }
  }

  return (
    <div className="h-screen bg-background text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <FiLock className="w-4.5 h-4.5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold leading-tight">Defina uma senha nova</div>
            <div className="text-[11.5px] text-muted-foreground leading-tight">
              {user?.username ? <>Olá, {user.fullName || user.username}. </> : null}
              Antes de continuar, escolha uma senha só sua.
            </div>
          </div>
        </div>

        <form onSubmit={envia} className="rounded-2xl glass-panel neu-bar p-4 space-y-3">
          <Campo rotulo="Senha atual" valor={atual} onChange={setAtual} autoFocus />
          <div>
            <Campo rotulo="Senha nova" valor={nova} onChange={setNova} />
            <div className={`mt-1 text-[10.5px] ${curta ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
              {curta ? `faltam ${MIN - nova.length} caractere(s)` : `pelo menos ${MIN} caracteres, diferente da atual`}
            </div>
          </div>
          <div>
            <Campo rotulo="Repita a senha nova" valor={confirma} onChange={setConfirma} />
            {diferente && <div className="mt-1 text-[10.5px] text-amber-600 dark:text-amber-400">as duas não estão iguais</div>}
          </div>

          {erro && (
            <div className="rounded-xl neu-card px-3 py-2 text-[11.5px] text-red-600 dark:text-red-400 flex items-start gap-2">
              <FiAlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {erro}
            </div>
          )}

          <button type="submit" disabled={!podeSalvar}
            className="w-full h-9 rounded-xl bg-primary text-primary-foreground neu-relief text-[12.5px] font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
            {salvando ? <><FiLoader className="w-3.5 h-3.5 animate-spin" /> Salvando…</> : <><FiCheck className="w-3.5 h-3.5" /> Salvar e continuar</>}
          </button>

          {/* Trocar a senha derruba as OUTRAS sessões da pessoa. Dizer isso aqui
              evita o susto de descobrir depois que o celular deslogou. */}
          <div className="text-[10px] text-muted-foreground leading-snug">
            Ao salvar, você continua conectado aqui e sai de qualquer outro
            aparelho onde a sua conta estiver aberta.
          </div>
        </form>

        <button onClick={() => logout()}
          className="mt-3 w-full h-8 rounded-xl text-[11.5px] text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5">
          <FiLogOut className="w-3.5 h-3.5" /> Sair
        </button>
      </div>
    </div>
  )
}

function Campo({ rotulo, valor, onChange, autoFocus }: {
  rotulo: string; valor: string; onChange: (v: string) => void; autoFocus?: boolean
}) {
  return (
    <label className="block">
      <span className="text-[10.5px] font-medium text-muted-foreground">{rotulo}</span>
      <input
        type="password" value={valor} autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)}
        autoComplete={rotulo === 'Senha atual' ? 'current-password' : 'new-password'}
        className="mt-1 w-full h-9 px-3 rounded-xl neu-inset border border-border bg-transparent text-[12.5px] focus:outline-none focus:ring-1 focus:ring-primary"
      />
    </label>
  )
}
